/**
 * Action handlers (a:call, a:stream).
 *
 * Background calls marked with `fireAndForget: true` return immediately
 * and log failures asynchronously so navigation can proceed without
 * waiting for the network round-trip.
 */

import type { FlowStep } from "../types/flow";
import type { ExecutionContext } from "../context/execution-context";
import { parsePath } from "../expressions/path";
import { resolveRecord, resolveValue } from "../expressions/resolve";
import { runSteps } from "../runtime/flow-runner";
import { registerStepHandler } from "../runtime/step-registry";
import type { StepResult } from "../runtime/types";

/**
 * Cast a FlowStep (discriminated union) to an indexable record.
 */
function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
 return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

const ENVELOPE_KEYS = ["data", "result", "state", "session"] as const;
const ENVELOPE_META_KEYS = new Set([
 "ok",
 "success",
 "queued",
 "status",
 "code",
 "requestId",
 "message",
 "error",
 "debug",
 "meta",
 "trace",
]);

function shouldUnwrapEnvelope(
 value: Record<string, unknown>,
 envelopeKey: string,
): boolean {
 if (!Object.prototype.hasOwnProperty.call(value, envelopeKey)) return false;

 const keys = Object.keys(value);
 if (keys.length ===1) return true;

 const remaining = keys.filter((key) => key !== envelopeKey);
 return remaining.every((key) => ENVELOPE_META_KEYS.has(key));
}

function normalizeOperationResult(value: unknown, depth =0): unknown {
 if (depth >=3 || !isPlainObject(value)) return value;

 const obj = value as Record<string, unknown>;
 for (const envelopeKey of ENVELOPE_KEYS) {
 if (shouldUnwrapEnvelope(obj, envelopeKey)) {
 return normalizeOperationResult(obj[envelopeKey], depth +1);
 }
 }
 return value;
}

function getOpKey(step: FlowStep, ctx: ExecutionContext): string | null {
 const r = asRecord(step);

 const directOp = resolveValue(r["op"], ctx);
 if (typeof directOp === "string" && directOp.trim()) {
 return directOp.trim();
 }

 const moduleName = resolveValue(r["module"], ctx);
 const operationName = resolveValue(r["operation"], ctx);

 if (typeof moduleName === "string" && typeof operationName === "string") {
 const op = `${moduleName}.${operationName}`.trim();
 return op ? op : null;
 }

 return null;
}

/**
 * Read a single user-id candidate, accepting either a non-empty string or a
 * positive finite number. Returns an empty string when the candidate is not
 * usable so callers can fall through to the next source.
 */
function readUserIdCandidate(value: unknown): string {
 if (typeof value === "string" && value.trim()) {
 return value.trim();
 }
 if (typeof value === "number" && Number.isFinite(value) && value >0) {
 return String(value);
 }
 return "";
}

/**
 * Best-effort decode of a JWT `sub` claim from an access token.
 *
 * This only decodes (it does NOT verify) the token so the client runtime can
 * derive a stable owner identifier for stateful operations when the session
 * blob does not expose a dedicated user-id field. The backend auth boundary
 * remains responsible for verifying identity; mental-content trusts the
 * provided `ownerId` exactly as it did before.
 */
function decodeJwtSubject(token: unknown): string {
 if (typeof token !== "string" || !token.trim()) return "";

 const parts = token.split(".");
 if (parts.length< 2) return "";

 try {
 const segment = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
 const padded = segment.padEnd(
 segment.length + ((4 - (segment.length %4)) %4),
 "=",
 );

 const decoded =
 typeof atob === "function"
 ? atob(padded)
 : Buffer.from(padded, "base64").toString("utf8");

 const claims = JSON.parse(decoded) as Record<string, unknown>;
 return (
 readUserIdCandidate(claims.sub) ||
 readUserIdCandidate(claims.userId) ||
 readUserIdCandidate(claims.user_id) ||
 readUserIdCandidate(claims.subject) ||
 readUserIdCandidate(claims.ownerId)
 );
 } catch {
 return "";
 }
}

/**
 * Resolve the authenticated owner identifier from the session snapshot.
 *
 * Stateful operations (e.g. mental-content.startGuideRun) require a verified
 * ownerId in addition to the access token. Authored flows intentionally avoid
 * repeating authentication plumbing, so the handler derives ownerId from the
 * execution session here.
 *
 * The canonical AuthCredentials shape stores a string `userId`, but apps that
 * persist their own session blob (e.g. under "mental2.session") may use a
 * different key ("sub", "user_id", "uid", "ownerId", "id", "subject",
 * "accountId"), a nested `user`/`account`/`profile` object, or only a JWT
 * `accessToken`. The previous strict top-level-key check silently skipped
 * ownerId injection for those sessions, which made ownerId-required
 * operations fail while read-only calls (where ownerId is optional) kept
 * working. Resolve common identifier keys, nested objects, and finally the
 * JWT `sub` claim so injection is robust across session shapes; preserve an
 * explicit ownerId from flows.
 */
function resolveOwnerIdFromSession(session: unknown): string {
 if (!isPlainObject(session)) return "";

 const candidates: unknown[] = [
 session.userId,
 session.user_id,
 session.sub,
 session.uid,
 session.ownerId,
 session.id,
 session.subject,
 session.accountId,
 ];

 for (const candidate of candidates) {
 const resolved = readUserIdCandidate(candidate);
 if (resolved) return resolved;
 }

 // Nested user/account/profile objects (e.g. session.user.id).
 const nestedRoots = [session.user, session.account, session.profile];
 for (const nested of nestedRoots) {
 if (!isPlainObject(nested)) continue;
 const resolved =
 readUserIdCandidate(nested.id) ||
 readUserIdCandidate(nested.userId) ||
 readUserIdCandidate(nested.user_id) ||
 readUserIdCandidate(nested.sub);
 if (resolved) return resolved;
 }

 // Last resort: derive the subject from an access token JWT.
 const tokenResolved = decodeJwtSubject(session.accessToken);
 if (tokenResolved) return tokenResolved;

 return "";
}

function getInput(step: FlowStep, ctx: ExecutionContext): Record<string, unknown> {
 const r = asRecord(step);
 const raw = r["in"] ?? r["input"];
 const input = isPlainObject(raw) ? resolveRecord(raw, ctx) : {};

 // Stateful operations require the authenticated owner identifier in
 // addition to the access token. Derive it from the execution session so
 // authored flows do not need to repeat authentication plumbing. Preserve
 // an explicit ownerId for compatibility with callers that provide one.
 if (input.ownerId === undefined) {
 const session = ctx.getScope("session");
 const ownerId = resolveOwnerIdFromSession(session);
 if (ownerId) input.ownerId = ownerId;
 }

 return input;
}

function writeOutput(step: FlowStep, ctx: ExecutionContext, value: unknown): void {
 const r = asRecord(step);
 const target = (r["out"] ?? r["output"]) as string | undefined;
 if (!target || typeof target !== "string") return;
 const parsed = parsePath(target);
 if (!parsed) return;
 ctx.writeState(parsed.scope, parsed.segments, value);
}

interface OperationFailure {
 status?: number;
 error?: unknown;
 data?: unknown;
}

/**
 * Derive a non-empty, human-readable failure detail from an operation result.
 *
 * Upstream services and proxies are inconsistent about the error envelope key.
 * NestJS-style responses commonly put the generic HTTP status label in `error`
 * ("Bad Request", "Internal Server Error") while the actionable detail lives
 * in `message`/`detail`, and `message` may itself be an array from
 * class-validator. We therefore prefer `message`/`detail`/`errorMessage` over
 * `error`, then fall back to the transport-extracted `res.error`, the raw body,
 * and finally a status-based message. Without this, failures from
 * `mental-content.startGuideRun` (e.g. missing ownerId, inactive Path) were
 * logged as `Operation "..." failed {}` with no actionable detail.
 */
function readOperationError(
 res: OperationFailure,
 opKey: string,
): string {
 if (isPlainObject(res.data)) {
 for (const key of ["message", "detail", "errorMessage", "error"]) {
 const value = res.data[key];
 if (typeof value === "string" && value.trim()) {
 return value.trim();
 }
 }

 if (Array.isArray(res.data.message)) {
 const joined = (res.data.message as readonly unknown[])
 .map((item) => (typeof item === "string" ? item.trim() : ""))
 .filter(Boolean)
 .join("; ")
 .trim();
 if (joined) return joined;
 }
 }

 if (typeof res.error === "string" && res.error.trim()) {
 return res.error.trim();
 }

 if (typeof res.data === "string" && res.data.trim()) {
 return res.data.trim();
 }

 if (typeof res.status === "number" && res.status >0) {
 return `Operation "${opKey}" failed with status ${res.status}`;
 }

 return `Operation "${opKey}" failed`;
}

function buildStatusSuffix(res: OperationFailure): string {
 return typeof res.status === "number" && res.status >0
 ? ` (status ${res.status})`
 : "";
}

registerStepHandler("a:call", async (step, ctx): Promise<StepResult | void> => {
 const opKey = getOpKey(step, ctx);
 if (!opKey) {
 ctx.logger.error(`a:call step is missing operation reference`);
 throw new Error(`a:call step is missing operation reference`);
 }

 const input = getInput(step, ctx);
 const fireAndForget = Boolean(resolveValue(asRecord(step)["fireAndForget"], ctx));
 const callOpts: { signal?: AbortSignal } = {};
 if (!fireAndForget && ctx.signal) callOpts.signal = ctx.signal;

 if (fireAndForget) {
 void Promise.resolve()
 .then(() => ctx.transport.call(opKey, input, callOpts))
 .then((res) => {
 if (!res.ok) {
 ctx.logger.warn(`Background operation "${opKey}" failed: ${readOperationError(res, opKey)}`, {
 status: res.status,
 error: res.error,
 data: res.data,
 });
 }
 })
 .catch((err) => {
 ctx.logger.warn(`Background operation "${opKey}" threw`, err);
 });

 return;
 }

 const res = await ctx.transport.call(opKey, input, callOpts);

 if (!res.ok) {
 const errorDetail = readOperationError(res, opKey);
 const statusSuffix = buildStatusSuffix(res);
 const errStepsRaw = asRecord(step)["err"];
 const hasInlineErrorBranch = Array.isArray(errStepsRaw);
 const logMeta = {
 status: res.status,
 error: res.error,
 data: res.data,
 operationId: opKey,
 };

 // An inline error branch makes the failure part of the authored flow.
 // User-correctable4xx responses should not be emitted through
 // console.error because Next.js presents those logs as application errors.
 if (
 hasInlineErrorBranch &&
 typeof res.status === "number" &&
 res.status >=400 &&
 res.status< 500
 ) {
 ctx.logger.info(
 `Operation "${opKey}" was rejected${statusSuffix}: ${errorDetail}`,
 logMeta,
 );
 } else if (hasInlineErrorBranch) {
 ctx.logger.warn(
 `Operation "${opKey}" failed${statusSuffix}: ${errorDetail}`,
 logMeta,
 );
 } else {
 ctx.logger.error(
 `Operation "${opKey}" failed${statusSuffix}: ${errorDetail}`,
 logMeta,
 );
 }

 const err = new Error(errorDetail) as Error & {
 status?: number;
 };
 err.status = res.status ??500;
 if (hasInlineErrorBranch) {
 return { error: err, errSteps: errStepsRaw as readonly FlowStep[] };
 }
 throw err;
 }

 const normalizedResult = normalizeOperationResult(res.data);
 writeOutput(step, ctx, normalizedResult);
 ctx.setResult(normalizedResult);
 return { result: normalizedResult };
});

registerStepHandler("a:stream", async (step, ctx): Promise<StepResult | void> => {
 if (!ctx.streamTransport) {
 throw new Error(`a:stream requires a streamTransport in ExecutionContext`);
 }

 const opKey = getOpKey(step, ctx);
 if (!opKey) {
 throw new Error(`a:stream step is missing operation reference`);
 }

 const input = getInput(step, ctx);
 const events = isPlainObject(asRecord(step)["events"])
 ? (asRecord(step)["events"] as Record<string, readonly FlowStep[]>)
 : {};

 const streamOpts: { signal?: AbortSignal } = {};
 if (ctx.signal) streamOpts.signal = ctx.signal;

 let lastPayload: unknown = undefined;

 try {
 for await (const evt of ctx.streamTransport.stream(opKey, input, streamOpts)) {
 ctx.setEvent(evt.data);
 lastPayload = evt.data;

 const handler = events[evt.event] ?? events["*"];
 if (Array.isArray(handler) && handler.length >0) {
 try {
 await runSteps(handler, ctx);
 } catch (err) {
 ctx.logger.warn(`stream event handler "${evt.event}" failed`, err);
 }
 }

 if (evt.event === "error") {
 ctx.setError(evt.data);
 throw new Error(
 typeof (evt.data as { message?: string })?.message === "string"
 ? (evt.data as { message: string }).message
 : `Stream "${opKey}" reported error`,
 );
 }
 }
 } finally {
 ctx.clearEvent();
 }

 writeOutput(step, ctx, lastPayload);
 ctx.setResult(lastPayload);
 return { result: lastPayload };
});